import React from "react";
import {
  getProgramDisplayName,
  isPrivateClientStrategyAlias,
  isStrategyAvailableForAdvisors,
} from "../../utils/proposalUtils";
import {
  createEmptyFeeUpdateAccount,
  getFeeUpdateAccountReferenceIssue,
  getFeeUpdateStrategyCompatibilityIssue,
  isFeeUpdateAccountComplete,
} from "./feeUpdateAccountUtils";
import {
  REGISTRATION_DETAIL_FIELD_DEFINITIONS,
  SOLO_K_OWNERSHIP_EVIDENCE_PROMPT,
  SOLO_K_PARTICIPANT_LIMIT_ALERT,
  SOLO_K_SPOUSE_QUESTION,
  exceedsSoloKParticipantLimit,
  getRegistrationRules,
  normalizeRegistrationDetails,
  shouldAskSoloKSpouseQuestion,
} from "./ispBusinessRules";
import { IspRegistrationOptions } from "./ispRegistrationOptions";
import { isStandardRegistrationStrategy } from "./standardRegistrationUtils";

const SOLO_K_EVIDENCE_OPTIONS = [
  "Operating Agreement",
  "Partnership Agreement",
  "Articles of Incorporation/Organization",
  "IRS EIN Confirmation Letter (Notice CP 575 G)",
];

const DetailInput = ({
  id,
  definition,
  value,
  onChange,
  columnClass = "col-md-6",
  numeric = false,
}) => (
  <div className={`form-group ${columnClass} mb-2`}>
    <label htmlFor={id}>{definition.label}</label>
    <input
      id={id}
      type="text"
      inputMode={numeric ? "numeric" : undefined}
      autoComplete={numeric ? "off" : undefined}
      className="form-control"
      maxLength={definition.maxLength}
      placeholder={definition.placeholder}
      value={value || ""}
      onChange={(event) =>
        onChange(
          numeric
            ? event.target.value.replace(/\D/g, "")
            : event.target.value,
        )
      }
    />
  </div>
);

const FeeUpdateRegistrationDetails = ({
  account,
  index,
  registrationRules,
  updateAccount,
}) => {
  const updateField = (field, value) => updateAccount(index, field, value);

  if (registrationRules.isMinorRegistration) {
    return (
      <div className="form-row" data-testid={`fee-update-minor-details-${index}`}>
        <DetailInput
          id={`feeUpdateCustodianFullName${index}`}
          definition={REGISTRATION_DETAIL_FIELD_DEFINITIONS.custodianFullName}
          value={account.custodianFullName}
          onChange={(value) => updateField("custodianFullName", value)}
        />
        <DetailInput
          id={`feeUpdateRelationshipToPrimary${index}`}
          definition={
            REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToPrimary
          }
          value={account.relationshipToPrimary}
          onChange={(value) => updateField("relationshipToPrimary", value)}
        />
      </div>
    );
  }

  if (registrationRules.asksTrustName) {
    return (
      <div className="form-row" data-testid={`fee-update-trust-details-${index}`}>
        <DetailInput
          id={`feeUpdateTrustName${index}`}
          definition={REGISTRATION_DETAIL_FIELD_DEFINITIONS.trustName}
          value={account.trustName}
          onChange={(value) => updateField("trustName", value)}
          columnClass="col-md-12"
        />
      </div>
    );
  }

  if (registrationRules.asksEstateDetails) {
    return (
      <div className="form-row" data-testid={`fee-update-estate-details-${index}`}>
        <DetailInput
          id={`feeUpdateDecedentName${index}`}
          definition={REGISTRATION_DETAIL_FIELD_DEFINITIONS.decedentName}
          value={account.decedentName}
          onChange={(value) => updateField("decedentName", value)}
        />
        <DetailInput
          id={`feeUpdateDecedentSsnLast4${index}`}
          definition={REGISTRATION_DETAIL_FIELD_DEFINITIONS.decedentSsnLast4}
          value={account.decedentSsnLast4}
          onChange={(value) => updateField("decedentSsnLast4", value)}
          columnClass="col-md-2"
          numeric
        />
        <DetailInput
          id={`feeUpdateRelationshipToExecutor${index}`}
          definition={
            REGISTRATION_DETAIL_FIELD_DEFINITIONS.relationshipToExecutor
          }
          value={account.relationshipToExecutor}
          onChange={(value) => updateField("relationshipToExecutor", value)}
          columnClass="col-md-4"
        />
      </div>
    );
  }

  if (registrationRules.asksBusinessDetails) {
    return (
      <div className="form-row" data-testid={`fee-update-business-details-${index}`}>
        <DetailInput
          id={`feeUpdateBusinessName${index}`}
          definition={REGISTRATION_DETAIL_FIELD_DEFINITIONS.businessName}
          value={account.businessName}
          onChange={(value) => updateField("businessName", value)}
          columnClass="col-md-8"
        />
        <DetailInput
          id={`feeUpdateBusinessEinLast4${index}`}
          definition={REGISTRATION_DETAIL_FIELD_DEFINITIONS.businessEinLast4}
          value={account.businessEinLast4}
          onChange={(value) => updateField("businessEinLast4", value)}
          columnClass="col-md-4"
          numeric
        />
      </div>
    );
  }

  if (!registrationRules.asksSoloKQuestionnaire) return null;

  const questionnaire = account.soloKQuestionnaire || {};
  const ownershipEvidence = Array.isArray(questionnaire.ownershipEvidence)
    ? questionnaire.ownershipEvidence
    : [];
  const updateSoloK = (field, value) => {
    const nextQuestionnaire = {
      ...questionnaire,
      [field]: value,
    };
    if (
      ["ownerCount", "employeeCount"].includes(field) &&
      !shouldAskSoloKSpouseQuestion(nextQuestionnaire)
    ) {
      nextQuestionnaire.secondParticipantIsSpouse = "";
    }
    updateField("soloKQuestionnaire", nextQuestionnaire);
  };
  const toggleEvidence = (option) =>
    updateSoloK(
      "ownershipEvidence",
      ownershipEvidence.includes(option)
        ? ownershipEvidence.filter((value) => value !== option)
        : [...ownershipEvidence, option],
    );
  const id = (suffix) => `feeUpdateSoloK${suffix}${index}`;
  const asksSpouseQuestion = shouldAskSoloKSpouseQuestion(questionnaire);
  const participantLimitExceeded =
    exceedsSoloKParticipantLimit(questionnaire);

  return (
    <div
      className="mb-2"
      data-testid={`fee-update-solo-k-details-${index}`}
    >
      <strong className="d-block mb-2">Solo(k) Questionnaire</strong>
      <div className="form-row">
        <div className="form-group col-md-6 mb-2">
          <label htmlFor={id("SponsoringBusiness")}>
            Name of Sponsoring Business
          </label>
          <input
            id={id("SponsoringBusiness")}
            type="text"
            className="form-control"
            placeholder="Client Name or DBA"
            value={questionnaire.sponsoringBusinessName || ""}
            onChange={(event) =>
              updateSoloK("sponsoringBusinessName", event.target.value)
            }
          />
        </div>
        <div className="form-group col-md-3 mb-2">
          <label htmlFor={id("OwnerCount")}>Number of Owners</label>
          <input
            id={id("OwnerCount")}
            type="number"
            min="1"
            className="form-control"
            value={questionnaire.ownerCount || ""}
            onChange={(event) =>
              updateSoloK(
                "ownerCount",
                event.target.value.replace(/\D/g, ""),
              )
            }
          />
        </div>
        <div className="form-group col-md-3 mb-2">
          <label htmlFor={id("EmployeeCount")}>Current Employees</label>
          <input
            id={id("EmployeeCount")}
            type="number"
            min="0"
            className="form-control"
            value={questionnaire.employeeCount ?? ""}
            onChange={(event) =>
              updateSoloK(
                "employeeCount",
                event.target.value.replace(/\D/g, ""),
              )
            }
          />
        </div>
      </div>
      {asksSpouseQuestion && (
        <fieldset className="mb-2">
          <legend className="h6 mb-1">{SOLO_K_SPOUSE_QUESTION}</legend>
          {["Yes", "No"].map((answer) => (
            <div className="form-check form-check-inline" key={answer}>
              <input
                id={id(`SecondParticipantIsSpouse${answer}`)}
                name={id("SecondParticipantIsSpouse")}
                type="radio"
                className="form-check-input"
                value={answer}
                checked={questionnaire.secondParticipantIsSpouse === answer}
                onChange={(event) =>
                  updateSoloK(
                    "secondParticipantIsSpouse",
                    event.target.value,
                  )
                }
              />
              <label
                className="form-check-label"
                htmlFor={id(`SecondParticipantIsSpouse${answer}`)}
              >
                {answer}
              </label>
            </div>
          ))}
        </fieldset>
      )}
      {participantLimitExceeded && (
        <div className="alert alert-danger py-2 mb-2" role="alert">
          {SOLO_K_PARTICIPANT_LIMIT_ALERT}
        </div>
      )}
      <div className="form-row">
        <div className="form-group col-md-6 mb-2">
          <label htmlFor={id("AnticipatesEmployees")}>
            Anticipates Hiring New Employees
          </label>
          <select
            id={id("AnticipatesEmployees")}
            className="form-control"
            value={questionnaire.anticipatesEmployees || ""}
            onChange={(event) =>
              updateSoloK("anticipatesEmployees", event.target.value)
            }
          >
            <option value="">Select</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
          </select>
        </div>
        <div className="form-group col-md-6 mb-2">
          <label htmlFor={id("PlanType")}>Plan Status</label>
          <select
            id={id("PlanType")}
            className="form-control"
            value={questionnaire.planType || ""}
            onChange={(event) =>
              updateSoloK("planType", event.target.value)
            }
          >
            <option value="">Select</option>
            <option value="New Plan">New Plan</option>
            <option value="Existing Plan">Existing Plan</option>
          </select>
        </div>
      </div>
      {questionnaire.planType === "Existing Plan" && (
        <div className="form-check fee-update-solo-k-check mb-2">
          <input
            id={id("ExistingPlanDocuments")}
            type="checkbox"
            className="form-check-input"
            checked={!!questionnaire.existingPlanDocumentsProvided}
            onChange={(event) =>
              updateSoloK(
                "existingPlanDocumentsProvided",
                event.target.checked,
              )
            }
          />
          <label
            className="form-check-label"
            htmlFor={id("ExistingPlanDocuments")}
          >
            Current plan documents have been requested/provided.
          </label>
        </div>
      )}
      <fieldset className="mb-2">
        <legend className="h6 mb-1">
          Evidence of Business Ownership
          <small className="text-muted font-weight-normal ml-1">
            {SOLO_K_OWNERSHIP_EVIDENCE_PROMPT}
          </small>
        </legend>
        {SOLO_K_EVIDENCE_OPTIONS.map((option) => {
          const optionId = id(
            `Evidence${option.replace(/[^A-Za-z0-9]/g, "")}`,
          );
          return (
            <div className="form-check fee-update-solo-k-check" key={option}>
              <input
                id={optionId}
                type="checkbox"
                className="form-check-input"
                checked={ownershipEvidence.includes(option)}
                onChange={() => toggleEvidence(option)}
              />
              <label className="form-check-label" htmlFor={optionId}>
                {option}
              </label>
            </div>
          );
        })}
      </fieldset>
      <strong className="d-block mb-1">Advisor Certifications</strong>
      <div className="form-check fee-update-solo-k-check mb-2">
        <input
          id={id("CertificationNoEmployees")}
          type="checkbox"
          className="form-check-input"
          checked={!!questionnaire.certificationNoEmployees}
          onChange={(event) =>
            updateSoloK("certificationNoEmployees", event.target.checked)
          }
        />
        <label
          className="form-check-label"
          htmlFor={id("CertificationNoEmployees")}
        >
          Advisor and client understand the Solo(k) employee limitation.
        </label>
      </div>
      <div className="form-check fee-update-solo-k-check mb-2">
        <input
          id={id("CertificationContributionLimits")}
          type="checkbox"
          className="form-check-input"
          checked={!!questionnaire.certificationContributionLimits}
          onChange={(event) =>
            updateSoloK(
              "certificationContributionLimits",
              event.target.checked,
            )
          }
        />
        <label
          className="form-check-label"
          htmlFor={id("CertificationContributionLimits")}
        >
          Advisor and client understand the cross-plan contribution limits.
        </label>
      </div>
    </div>
  );
};

const FeeUpdateAccountEditor = ({
  accounts = [],
  lists,
  advisorName,
  additionalAdvisors = [],
  pcsRegistrationType = "Single",
  onPcsRegistrationTypeChange = () => {},
  standardRegistrationType = "Single",
  onStandardRegistrationTypeChange = () => {},
  onChange,
  showJointAccountHolderWarning = false,
}) => {
  const proposalTypes = lists?.proposalTypes || [];
  const advisorCandidates = [advisorName, ...additionalAdvisors];
  const availableStrategies = proposalTypes
    .filter(
      (proposalType) =>
        proposalType.name &&
        proposalType.enabled !== false &&
        isStrategyAvailableForAdvisors(proposalType, advisorCandidates),
    )
    .sort((a, b) => a.name.localeCompare(b.name));
  const compatibilityIssue = getFeeUpdateStrategyCompatibilityIssue(accounts);
  const referenceOptions = {
    registrationTypes: lists?.registrationTypes || [],
    proposalTypes,
    advisorNames: advisorCandidates,
  };
  const referenceIssue = getFeeUpdateAccountReferenceIssue(
    accounts,
    referenceOptions,
  );
  const isPcsFeeUpdate =
    accounts.length > 0 &&
    accounts.every(
      (account) =>
        !!String(account?.strategy || "").trim() &&
        isPrivateClientStrategyAlias(account.strategy, proposalTypes),
    );
  const isPcsMultiRegistration =
    isPcsFeeUpdate && pcsRegistrationType === "Multi";
  const standardStrategies = Array.from(
    new Set(
      accounts
        .map((account) => String(account?.strategy || "").trim())
        .filter(Boolean),
    ),
  );
  const isStandardFeeUpdate =
    accounts.length > 0 &&
    standardStrategies.length === 1 &&
    accounts.every((account) =>
      isStandardRegistrationStrategy(account?.strategy, proposalTypes),
    );
  const isStandardMultiRegistration =
    isStandardFeeUpdate && standardRegistrationType === "Multi";
  const isAnyMultiRegistration =
    isPcsMultiRegistration || isStandardMultiRegistration;
  const usesSharedStrategy =
    isPcsFeeUpdate || isStandardMultiRegistration;

  const updateAccount = (index, field, value) => {
    const applyingSharedStrategy =
      field === "strategy" &&
      (isPrivateClientStrategyAlias(value, proposalTypes) ||
        isStandardMultiRegistration);
    onChange(
      accounts.map((account, accountIndex) => {
        if (applyingSharedStrategy) {
          return { ...account, strategy: value };
        }
        if (accountIndex !== index) return account;
        const updated = { ...account, [field]: value };
        return field === "registration"
          ? {
              ...updated,
              ...normalizeRegistrationDetails(
                updated,
                value,
                lists?.registrationTypes || [],
              ),
            }
          : updated;
      }),
    );
  };
  const updateAllStrategies = (strategy) =>
    onChange(accounts.map((account) => ({ ...account, strategy })));
  const addAccount = () =>
    onChange([
      ...accounts,
      {
        ...createEmptyFeeUpdateAccount(),
        strategy: usesSharedStrategy ? accounts[0]?.strategy || "" : "",
      },
    ]);
  const removeAccount = (index) => {
    if (accounts.length <= 1) return;
    onChange(accounts.filter((_, accountIndex) => accountIndex !== index));
  };

  return (
    <div className="mt-2" data-testid="fee-update-account-editor">
      <label className="d-block mb-1">
        <strong>Fee Update Accounts</strong>
      </label>
      <small className="form-text text-muted mb-2">
        {isAnyMultiRegistration
          ? "Enter one registration per row. Account Holder Name, Last 4 Digits of Account Number, and Registration Type are required; Strategy applies to every row. The registration-specific investment questions appear in Account Information."
          : isPcsFeeUpdate
            ? "Enter the PCS account's Last 4 Digits of Account Number and Registration Type. Strategy applies to this registration."
            : "Enter one account per row. Last 4 Digits of Account Number, Registration, and Strategy are required for every account."}
      </small>

      {isPcsFeeUpdate && (
        <>
          <div className="form-group mb-2" style={{ maxWidth: "520px" }}>
            <label htmlFor="pcsFeeUpdateStrategy">
              <strong>Strategy (applies to all registrations)</strong>
            </label>
            <select
              id="pcsFeeUpdateStrategy"
              aria-label="PCS Fee Update Strategy"
              className="form-control"
              value={accounts[0]?.strategy || ""}
              onChange={(event) => updateAllStrategies(event.target.value)}
            >
              <option value="">Select Strategy</option>
              {availableStrategies.map((proposalType) => (
                <option key={proposalType.name} value={proposalType.name}>
                  {getProgramDisplayName(proposalType.name, proposalTypes)}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group mb-2">
            <label className="d-block">
              <strong>Registration Type</strong>
            </label>
            {["Single", "Multi"].map((option) => (
              <div className="form-check form-check-inline" key={option}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="pcsFeeUpdateRegistrationType"
                  id={`pcsFeeUpdateRegistrationType${option}`}
                  aria-label={`PCS Registration Type - ${option}`}
                  value={option}
                  checked={pcsRegistrationType === option}
                  onChange={(event) =>
                    onPcsRegistrationTypeChange(event.target.value)
                  }
                />
                <label
                  className="form-check-label"
                  htmlFor={`pcsFeeUpdateRegistrationType${option}`}
                >
                  {option}
                </label>
              </div>
            ))}
          </div>
        </>
      )}

      {isStandardFeeUpdate && (
        <div className="form-group mb-2">
          <label className="d-block">
            <strong>Registration Type</strong>
          </label>
          {["Single", "Multi"].map((option) => (
            <div className="form-check form-check-inline" key={option}>
              <input
                className="form-check-input"
                type="radio"
                name="standardFeeUpdateRegistrationType"
                id={`standardFeeUpdateRegistrationType${option}`}
                aria-label={`Registration Type - ${option}`}
                value={option}
                checked={standardRegistrationType === option}
                onChange={(event) =>
                  onStandardRegistrationTypeChange(event.target.value)
                }
              />
              <label
                className="form-check-label"
                htmlFor={`standardFeeUpdateRegistrationType${option}`}
              >
                {option}
              </label>
            </div>
          ))}
        </div>
      )}

      {isStandardMultiRegistration && (
        <div className="form-group mb-2" style={{ maxWidth: "520px" }}>
          <label htmlFor="standardFeeUpdateStrategy">
            <strong>Strategy (applies to all registrations)</strong>
          </label>
          <select
            id="standardFeeUpdateStrategy"
            className="form-control"
            value={accounts[0]?.strategy || ""}
            onChange={(event) => updateAllStrategies(event.target.value)}
          >
            <option value="">Select Strategy</option>
            {availableStrategies.map((proposalType) => (
              <option key={proposalType.name} value={proposalType.name}>
                {getProgramDisplayName(proposalType.name, proposalTypes)}
              </option>
            ))}
          </select>
        </div>
      )}

      {accounts.map((account, index) => {
        const registrationRules = getRegistrationRules(
          account.registration,
          "",
          {
            requestType: "Existing",
            existingRequestAction: "Fee Update",
            registrationTypes: lists?.registrationTypes || [],
          },
        );
        const rowReferenceIssue = getFeeUpdateAccountReferenceIssue(
          [account],
          referenceOptions,
        );
        const rowComplete =
          isFeeUpdateAccountComplete(account, {
            requireAccountOwner: isAnyMultiRegistration,
          }) && !rowReferenceIssue;
        const strategyOptions = [...availableStrategies];
        if (
          account.strategy &&
          !strategyOptions.some(
            (proposalType) => proposalType.name === account.strategy,
          )
        ) {
          strategyOptions.unshift({ name: account.strategy, unavailable: true });
        }

        return (
          <div
            key={account._rowId || index}
            className="mb-2"
            data-testid={`fee-update-account-row-${index}`}
            style={{
              borderLeft: `5px solid ${rowComplete ? "green" : "#ced4da"}`,
              paddingLeft: "12px",
            }}
          >
            <div className="form-row align-items-end">
              {isAnyMultiRegistration && (
                <div className="form-group col-md-3 mb-2">
                  <label htmlFor={`feeUpdateAccountOwner${index}`}>
                    Account Holder Name
                  </label>
                  <input
                    id={`feeUpdateAccountOwner${index}`}
                    type="text"
                    className="form-control"
                    maxLength={160}
                    placeholder="Account holder name"
                    value={account.accountOwner || ""}
                    onChange={(event) =>
                      updateAccount(index, "accountOwner", event.target.value)
                    }
                  />
                </div>
              )}
              <div
                className={`form-group ${
                  usesSharedStrategy ? "col-md-3" : "col-md-2"
                } mb-2`}
              >
                <label htmlFor={`feeUpdateAccountLast4${index}`}>
                  Last 4 Digits of Account Number
                </label>
                <input
                  id={`feeUpdateAccountLast4${index}`}
                  type="text"
                  inputMode="numeric"
                  className="form-control"
                  maxLength={4}
                  placeholder="####"
                  value={account.accountLast4 || ""}
                  onChange={(event) =>
                    updateAccount(
                      index,
                      "accountLast4",
                      event.target.value.replace(/\D/g, ""),
                    )
                  }
                />
              </div>
              <div
                className={`form-group ${
                  isAnyMultiRegistration
                    ? "col-md-5"
                    : usesSharedStrategy
                      ? "col-md-8"
                      : "col-md-4"
                } mb-2`}
              >
                <label htmlFor={`feeUpdateRegistration${index}`}>
                  {usesSharedStrategy ? "Registration Type" : "Registration"}
                </label>
                <select
                  id={`feeUpdateRegistration${index}`}
                  className="form-control"
                  value={account.registration || ""}
                  onChange={(event) =>
                    updateAccount(index, "registration", event.target.value)
                  }
                >
                  <IspRegistrationOptions
                    registrationTypes={lists?.registrationTypes || []}
                    registrationCategoryTypes={
                      lists?.registrationCategoryTypes
                    }
                    selectedValue={account.registration}
                  />
                </select>
              </div>
              {!usesSharedStrategy && (
              <div className="form-group col-md-5 mb-2">
                <label htmlFor={`feeUpdateStrategy${index}`}>Strategy</label>
                <select
                  id={`feeUpdateStrategy${index}`}
                  className="form-control"
                  value={account.strategy || ""}
                  onChange={(event) =>
                    updateAccount(index, "strategy", event.target.value)
                  }
                >
                  <option value="">Select Strategy</option>
                  {strategyOptions.map((proposalType) => (
                    <option
                      key={proposalType.name}
                      value={proposalType.name}
                      disabled={proposalType.unavailable}
                    >
                      {getProgramDisplayName(proposalType.name, proposalTypes)}
                      {proposalType.unavailable ? " (Unavailable)" : ""}
                    </option>
                  ))}
                </select>
              </div>
              )}
              <div className="form-group col-md-1 mb-2">
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ padding: "6px 8px" }}
                  onClick={() => removeAccount(index)}
                  disabled={accounts.length <= 1}
                  title={accounts.length <= 1 ? "At least one account is required" : "Remove account"}
                  aria-label={`Remove fee update account ${index + 1}`}
                >
                  <i className="fa fa-trash" style={{ fontSize: "12px" }} />
                </button>
              </div>
            </div>
            {!isAnyMultiRegistration && (
              <FeeUpdateRegistrationDetails
                account={account}
                index={index}
                registrationRules={registrationRules}
                updateAccount={updateAccount}
              />
            )}
          </div>
        );
      })}

      {(!isPcsFeeUpdate || isPcsMultiRegistration) && (
        <button
          type="button"
          className="btn btn-outline-primary mb-2"
          onClick={addAccount}
        >
          {isAnyMultiRegistration ? "+ Add Registration" : "+ Add Account"}
        </button>
      )}

      {showJointAccountHolderWarning && (
        <small className="form-text text-danger mb-2" role="alert">
          Please provide joint account holder's information
        </small>
      )}

      {compatibilityIssue && (
        <div className="alert alert-danger py-2 mt-2 mb-2" role="alert">
          {compatibilityIssue}
        </div>
      )}

      {referenceIssue && (
        <div className="alert alert-danger py-2 mt-2 mb-2" role="alert">
          {referenceIssue}
        </div>
      )}
    </div>
  );
};

export default FeeUpdateAccountEditor;
